/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from "react";
import {
  CalendarDays,
  Plus,
  Trash2,
  Pencil,
  AlertTriangle,
  Check,
  X,
  Image as ImageIcon,
  Type as TypeIcon,
  ListChecks,
  Upload,
  Loader2,
  RefreshCw,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";
import { PlayerSelect } from "@/components/PlayerSelect";
import { ClubSelect } from "@/components/ClubSelect";
import { NationSelect } from "@/components/NationSelect";

// ── Types ─────────────────────────────────────────────────────────────────────

type DcType = "TEXT_ANSWER" | "MULTIPLE_CHOICE";

// Drives which dropdown is shown for TEXT_ANSWER challenges:
// PLAYER (legacy default), CLUB, NATION, FREE_TEXT (plain typing).
type DcAnswerType = "PLAYER" | "CLUB" | "NATION" | "FREE_TEXT";

// Admin-set rating surfaced as a coloured pill in mobile/web. Null = unrated.
type DcDifficulty = "EASY" | "MEDIUM" | "HARD";

interface DailyChallenge {
  id: number;
  scheduledDate: string; // YYYY-MM-DD
  type: DcType;
  title: string;
  prompt: string;
  imageUrl: string | null;
  /** Clean image shown to the mobile user ONLY after they answer correctly.
   *  Useful for "guess the blurred player" style challenges — imageUrl holds
   *  the teaser (blurred/silhouetted) and this holds the clean reveal. */
  revealImageUrl: string | null;
  optionsJson: string | null;
  correctAnswer: string;
  correctAnswerType?: DcAnswerType;
  answerAliases: string | null;
  cluesJson: string | null;
  maxPoints: number;
  difficulty: DcDifficulty | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { completions: number };
}

interface WeekStatus {
  days: { date: string; challenge: { scheduledDate: string; title: string; type: DcType } | null }[];
  missingCount: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatShortDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const formatDayLabel = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
    day: d.getDate(),
    month: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
  };
};

const safeJsonArray = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
};

// ── Form state ────────────────────────────────────────────────────────────────

interface FormState {
  scheduledDate: string;
  type: DcType;
  title: string;
  prompt: string;
  imageUrl: string;
  revealImageUrl: string;
  options: string[];
  correctOptionIndex: number;
  correctText: string;
  // What the question is asking about — drives which dropdown shows up.
  // Only relevant when type === 'TEXT_ANSWER'.
  answerType: DcAnswerType;
  aliases: string; // comma-separated
  clues: string; // newline-separated
  maxPoints: number;
  difficulty: DcDifficulty | null;
}

const blankForm = (): FormState => ({
  scheduledDate: todayISO(),
  type: "TEXT_ANSWER",
  title: "",
  prompt: "",
  imageUrl: "",
  revealImageUrl: "",
  options: ["", ""],
  correctOptionIndex: 0,
  correctText: "",
  answerType: "PLAYER",
  aliases: "",
  clues: "",
  maxPoints: 10,
  difficulty: null,
});

const fromChallenge = (c: DailyChallenge): FormState => {
  const options = safeJsonArray(c.optionsJson);
  return {
    scheduledDate: c.scheduledDate,
    type: c.type,
    title: c.title,
    prompt: c.prompt,
    imageUrl: c.imageUrl ?? "",
    revealImageUrl: c.revealImageUrl ?? "",
    options: options.length > 0 ? options : ["", ""],
    correctOptionIndex:
      c.type === "MULTIPLE_CHOICE" ? Math.max(0, parseInt(c.correctAnswer, 10) || 0) : 0,
    correctText: c.type === "TEXT_ANSWER" ? c.correctAnswer : "",
    answerType: c.correctAnswerType ?? "PLAYER",
    aliases: safeJsonArray(c.answerAliases).join(", "),
    clues: safeJsonArray(c.cluesJson).join("\n"),
    maxPoints: c.maxPoints,
    difficulty: c.difficulty ?? null,
  };
};

interface SubmitPayload {
  scheduledDate: string;
  type: DcType;
  title: string;
  prompt: string;
  imageUrl?: string | null;
  revealImageUrl?: string | null;
  options?: string[];
  correctAnswer: string;
  correctAnswerType?: DcAnswerType;
  answerAliases?: string[];
  clues?: string[];
  maxPoints?: number;
  difficulty?: DcDifficulty | null;
}

// Returns null on success, an error message string on validation failure.
const validateAndSerialize = (f: FormState): SubmitPayload | string => {
  if (!f.scheduledDate) return "Pick a scheduled date";
  if (!f.title.trim()) return "Title is required";

  if (f.type === "MULTIPLE_CHOICE") {
    const clean = f.options.map((o) => o.trim()).filter(Boolean);
    if (clean.length < 2) return "Add at least 2 options";
    if (clean.length > 6) return "At most 6 options";
    if (f.correctOptionIndex < 0 || f.correctOptionIndex >= clean.length)
      return "Select the correct option";
    return {
      scheduledDate: f.scheduledDate,
      type: f.type,
      title: f.title.trim(),
      prompt: f.prompt.trim(),
      imageUrl: f.imageUrl.trim() || undefined,
      revealImageUrl: f.revealImageUrl.trim() || undefined,
      options: clean,
      correctAnswer: String(f.correctOptionIndex),
      clues: f.clues
        .split("\n")
        .map((c) => c.trim())
        .filter(Boolean),
      maxPoints: f.maxPoints,
      difficulty: f.difficulty,
    };
  }

  if (!f.correctText.trim()) return "Correct answer is required";
  return {
    scheduledDate: f.scheduledDate,
    type: f.type,
    title: f.title.trim(),
    prompt: f.prompt.trim(),
    imageUrl: f.imageUrl.trim() || undefined,
    revealImageUrl: f.revealImageUrl.trim() || undefined,
    correctAnswer: f.correctText.trim(),
    correctAnswerType: f.answerType,
    answerAliases: f.aliases
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean),
    clues: f.clues
      .split("\n")
      .map((c) => c.trim())
      .filter(Boolean),
    maxPoints: f.maxPoints,
    difficulty: f.difficulty,
  };
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DailyChallengePage() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DailyChallenge | null>(null);
  // Date the "+ Add" button on the 7-day strip prefilled with, or null for
  // the toolbar "New Challenge" button (defaults to today).
  const [prefillDate, setPrefillDate] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["dc", "list"] });
    void qc.invalidateQueries({ queryKey: ["dc", "week-status"] });
  };

  // ── Queries ───────────────────────────────────────────────────────────────

  const {
    data: challenges = [],
    isLoading,
    refetch: refetchChallenges,
    isFetching: fetchingChallenges,
  } = useQuery<DailyChallenge[]>({
    queryKey: ["dc", "list"],
    queryFn: () => apiClient.get(API_ENDPOINTS.DAILY_CHALLENGE.LIST),
    select: (r: any) => (Array.isArray(r) ? r : []),
  });

  const { data: weekStatus } = useQuery<WeekStatus>({
    queryKey: ["dc", "week-status"],
    queryFn: () => apiClient.get(API_ENDPOINTS.DAILY_CHALLENGE.WEEK_STATUS),
    // Always give us a well-formed { days, missingCount } so the banner +
    // 7-day strip can call .map/.filter without defensive undefined checks
    // at every render. An error shape from the server coerces to empty.
    select: (r: any) => ({
      days: Array.isArray(r?.days) ? r.days : [],
      missingCount: typeof r?.missingCount === "number" ? r.missingCount : 0,
    }),
    refetchInterval: 60_000,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────

  const { mutate: createChallenge, isPending: creating } = useMutation({
    mutationFn: (payload: SubmitPayload) =>
      apiClient.post(API_ENDPOINTS.DAILY_CHALLENGE.CREATE, payload),
    onSuccess: () => {
      toast.success("Challenge scheduled");
      setDialogOpen(false);
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || "Failed to create challenge"),
  });

  const { mutate: updateChallenge, isPending: updating } = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SubmitPayload }) =>
      apiClient.patch(API_ENDPOINTS.DAILY_CHALLENGE.UPDATE(id), payload),
    onSuccess: () => {
      toast.success("Challenge updated");
      setDialogOpen(false);
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || "Failed to update"),
  });

  const { mutate: deleteChallenge } = useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(API_ENDPOINTS.DAILY_CHALLENGE.DELETE(id)),
    onSuccess: () => {
      toast.success("Challenge deleted");
      invalidate();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || "Failed to delete"),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const openCreate = (date?: string) => {
    setEditing(null);
    setPrefillDate(date ?? null);
    setDialogOpen(true);
  };

  const openEdit = (c: DailyChallenge) => {
    setEditing(c);
    setPrefillDate(null);
    setDialogOpen(true);
  };

  // Called by the dialog with an already-validated payload. Keeping this
  // thin lets the dialog own its own form state, so keystrokes don't
  // re-render the page (list + 7-day strip + week-status banner).
  const handleSubmit = (payload: SubmitPayload) => {
    if (editing) {
      updateChallenge({ id: editing.id, payload });
    } else {
      createChallenge(payload);
    }
  };

  const handleDelete = (c: DailyChallenge) => {
    if (!confirm(`Delete the challenge on ${formatShortDate(c.scheduledDate)}?\n"${c.title}"\n\nThis removes all user attempts for it.`)) return;
    deleteChallenge(c.id);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const sorted = useMemo(
    () => [...challenges].sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate)),
    [challenges],
  );

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <CalendarDays className="h-5 w-5 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Daily Challenge</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            One challenge per day. Users submit one answer; streak rewards consecutive correct solves.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              Promise.all([
                refetchChallenges(),
                qc.invalidateQueries({ queryKey: ["dc", "week-status"] }),
              ]).then(() => toast.success("Refreshed"));
            }}
            disabled={fetchingChallenges}
            title="Refresh daily challenges"
          >
            <RefreshCw
              className={`h-4 w-4 ${fetchingChallenges ? "animate-spin" : ""}`}
            />
          </Button>
          <Button
            onClick={() => openCreate()}
            className="bg-yellow-500 hover:bg-yellow-600 text-white gap-2"
          >
            <Plus className="h-4 w-4" /> New Challenge
          </Button>
        </div>
      </div>

      {/* Empty-week warning */}
      {weekStatus && Array.isArray(weekStatus.days) && weekStatus.missingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {weekStatus.missingCount} of the next 7 days {weekStatus.missingCount === 1 ? "has" : "have"} no challenge
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Missing dates:{" "}
              {weekStatus.days
                .filter((d) => !d.challenge)
                .map((d) => formatShortDate(d.date))
                .join(" · ")}
            </p>
          </div>
        </div>
      )}

      {/* 7-day preview strip */}
      {weekStatus && Array.isArray(weekStatus.days) && weekStatus.days.length > 0 && (
        <div className="mb-8">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Upcoming Week
          </p>
          <div className="grid grid-cols-7 gap-2">
            {weekStatus.days.map((d) => {
              const dayLabels = formatDayLabel(d.date);
              const filled = !!d.challenge;
              return (
                <button
                  key={d.date}
                  onClick={() => {
                    if (filled) {
                      const full = challenges.find((c) => c.scheduledDate === d.date);
                      if (full) openEdit(full);
                    } else {
                      openCreate(d.date);
                    }
                  }}
                  className={cn(
                    "rounded-xl border p-3 text-left transition-all hover:shadow-sm",
                    filled
                      ? "bg-white border-green-200 hover:border-green-400"
                      : "bg-gray-50 border-dashed border-gray-300 hover:border-amber-400 hover:bg-amber-50",
                  )}
                >
                  <p className="text-[10px] font-semibold text-gray-400 tracking-wide">
                    {dayLabels.weekday}
                  </p>
                  <p className="text-xl font-extrabold text-gray-900 leading-tight">
                    {dayLabels.day}
                  </p>
                  <p className="text-[10px] text-gray-400 mb-2">{dayLabels.month}</p>
                  {filled ? (
                    <>
                      <div className="flex items-center gap-1 mb-1">
                        <Check className="h-3 w-3 text-green-600" />
                        <span className="text-[10px] font-semibold text-green-700 uppercase">
                          Scheduled
                        </span>
                      </div>
                      <p className="text-xs font-medium text-gray-800 line-clamp-2">
                        {d.challenge!.title}
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center gap-1 text-amber-600">
                      <Plus className="h-3 w-3" />
                      <span className="text-[10px] font-semibold uppercase">Add</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* All challenges list */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">All Scheduled Challenges</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {challenges.length} total
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-16 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          </div>
        ) : challenges.length === 0 ? (
          <div className="py-16 text-center">
            <CalendarDays className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm mb-1 font-medium">No challenges yet</p>
            <p className="text-gray-400 text-xs mb-5">
              Schedule your first one to get users solving
            </p>
            <Button onClick={() => openCreate()} className="bg-yellow-500 hover:bg-yellow-600 text-white gap-2">
              <Plus className="h-4 w-4" /> New Challenge
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-12 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 px-5 py-2.5 border-b">
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-5">Title</div>
            <div className="col-span-2 text-center">Completions</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
        )}

        {sorted.map((c, i) => (
          <div
            key={c.id}
            className={cn(
              "grid grid-cols-12 px-5 py-3.5 items-center gap-2 text-sm",
              i !== sorted.length - 1 && "border-b border-gray-100",
              "hover:bg-gray-50 transition-colors",
            )}
          >
            <div className="col-span-2">
              <p className="font-semibold text-gray-900">{formatShortDate(c.scheduledDate)}</p>
              <p className="text-xs text-gray-400 font-mono">{c.scheduledDate}</p>
            </div>
            <div className="col-span-2">
              <TypePill type={c.type} />
            </div>
            <div className="col-span-5 min-w-0">
              <p className="font-medium text-gray-900 truncate">{c.title}</p>
              <p className="text-xs text-gray-500 truncate">{c.prompt}</p>
            </div>
            <div className="col-span-2 text-center">
              <Badge className="bg-blue-50 text-blue-700 font-semibold">
                {c._count?.completions ?? 0}
              </Badge>
            </div>
            <div className="col-span-1 flex justify-end gap-1">
              <button
                onClick={() => openEdit(c)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handleDelete(c)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit Dialog */}
      <ChallengeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        prefillDate={prefillDate}
        onSubmit={handleSubmit}
        isSaving={creating || updating}
      />
    </Layout>
  );
}

// ── Image upload block (reused for teaser + reveal images) ─────────────────

function ImageUploadBlock({
  label,
  hint,
  target,
  value,
  setValue,
  uploading,
  onUpload,
}: {
  label: string;
  hint: string;
  target: "imageUrl" | "revealImageUrl";
  value: string;
  setValue: (v: string) => void;
  uploading: boolean;
  onUpload: (file: File | null | undefined) => void;
}) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold text-gray-700 flex items-center gap-1">
        <ImageIcon className="h-3 w-3" /> {label}{" "}
        <span className="font-normal text-gray-400">(optional)</span>
      </Label>
      <p className="text-[11px] text-gray-400 mb-2 leading-relaxed">{hint}</p>

      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center max-h-56">
          <img src={value} alt="preview" className="max-h-56 object-contain" />
          <button
            type="button"
            onClick={() => setValue("")}
            className="absolute top-2 right-2 bg-white/95 hover:bg-white rounded-full p-1.5 shadow border border-gray-200"
            title="Remove image"
          >
            <X className="h-3.5 w-3.5 text-gray-700" />
          </button>
        </div>
      ) : (
        <label
          className={cn(
            "flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors",
            uploading
              ? "border-yellow-300 bg-yellow-50 cursor-wait"
              : "border-gray-300 hover:border-yellow-400 hover:bg-yellow-50",
          )}
        >
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              onUpload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 text-yellow-600 animate-spin" />
              <p className="text-sm font-medium text-yellow-700">Uploading…</p>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Upload className="h-5 w-5 text-yellow-600" />
              </div>
              <p className="text-sm font-medium text-gray-700">
                Click to upload {target === "revealImageUrl" ? "the reveal image" : "an image"}
              </p>
              <p className="text-[11px] text-gray-400">
                PNG, JPG, GIF or WebP · up to 5 MB
              </p>
            </>
          )}
        </label>
      )}

      <div className="mt-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Or paste an image URL…"
          className="text-xs"
        />
      </div>
    </div>
  );
}

// ── Type pill ────────────────────────────────────────────────────────────────

function TypePill({ type }: { type: DcType }) {
  if (type === "MULTIPLE_CHOICE") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
        <ListChecks className="h-3 w-3" /> Multiple Choice
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">
      <TypeIcon className="h-3 w-3" /> Text Answer
    </span>
  );
}

// ── Dialog ───────────────────────────────────────────────────────────────────

const ChallengeDialog = React.memo(function ChallengeDialog({
  open,
  onOpenChange,
  editing,
  prefillDate,
  onSubmit,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: DailyChallenge | null;
  prefillDate: string | null;
  onSubmit: (payload: SubmitPayload) => void;
  isSaving: boolean;
}) {
  // Form state lives here (not in the page) so each keystroke only re-renders
  // the dialog — the list, week-status banner, and 7-day strip stay still.
  const [form, setForm] = useState<FormState>(blankForm());

  // Tracks which image field (if any) is currently mid-upload so the two
  // upload slots show their own spinner state independently.
  const [uploading, setUploading] = useState<null | "imageUrl" | "revealImageUrl">(
    null,
  );

  // Seed the form whenever the dialog opens. Depending only on `open` keeps
  // mid-edit prop changes from clobbering user input.
  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? fromChallenge(editing)
        : { ...blankForm(), scheduledDate: prefillDate ?? todayISO() },
    );
    setUploading(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset correctOptionIndex if it points out of range after options shrink
  useEffect(() => {
    if (
      form.type === "MULTIPLE_CHOICE" &&
      form.correctOptionIndex >= form.options.length
    ) {
      setForm((f) => ({ ...f, correctOptionIndex: 0 }));
    }
  }, [form.options.length, form.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Functional updaters everywhere — the dialog re-renders on every keystroke,
  // so closures captured by handlers from one render must not stomp the
  // result of a faster handler from a later render.
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onUpload = async (
    file: File | null | undefined,
    target: "imageUrl" | "revealImageUrl",
  ) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = (await apiClient.post(
        API_ENDPOINTS.DAILY_CHALLENGE.UPLOAD_IMAGE,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      )) as { url: string };
      setForm((f) => ({ ...f, [target]: res.url }));
      toast.success("Image uploaded");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const addOption = () =>
    setForm((f) =>
      f.options.length >= 6 ? f : { ...f, options: [...f.options, ""] },
    );

  const removeOption = (idx: number) =>
    setForm((f) => {
      if (f.options.length <= 2) return f;
      const next = f.options.filter((_, i) => i !== idx);
      return {
        ...f,
        options: next,
        correctOptionIndex: Math.min(f.correctOptionIndex, next.length - 1),
      };
    });

  const setOption = (idx: number, value: string) =>
    setForm((f) => {
      const next = [...f.options];
      next[idx] = value;
      return { ...f, options: next };
    });

  // Validate locally then hand the page a payload — the page only needs to
  // know "create vs edit" and which mutation to fire.
  const handleSubmit = () => {
    const payload = validateAndSerialize(form);
    if (typeof payload === "string") {
      toast.error(payload);
      return;
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-yellow-500" />
            {editing ? "Edit Challenge" : "New Challenge"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Date + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
                Scheduled Date <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={form.scheduledDate}
                onChange={(e) => setField("scheduledDate", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
                Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.type}
                onValueChange={(v) => setField("type", v as DcType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TEXT_ANSWER">Text Answer</SelectItem>
                  <SelectItem value="MULTIPLE_CHOICE">Multiple Choice</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title */}
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Guess the player"
              maxLength={80}
            />
          </div>

          {/* Prompt */}
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
              Prompt <span className="font-normal text-gray-400">(optional)</span>
            </Label>
            <Textarea
              value={form.prompt}
              onChange={(e) => setField("prompt", e.target.value)}
              rows={3}
              className="resize-none"
              placeholder={
                form.type === "TEXT_ANSWER"
                  ? "Portuguese forward. 5× Ballon d'Or. Goes 'Siuuuu!'"
                  : "Which of these has NEVER won the UCL?"
              }
            />
          </div>

          {/* Image (optional) — this is the teaser image shown to the user
              BEFORE they answer. For "guess the player" style challenges,
              upload a blurred/silhouetted version here. */}
          <ImageUploadBlock
            label="Image (teaser)"
            hint="Shown to users while they guess. For blurred-player challenges, upload the blurred version here."
            target="imageUrl"
            value={form.imageUrl}
            setValue={(v) => setField("imageUrl", v)}
            uploading={uploading === "imageUrl"}
            onUpload={(f) => void onUpload(f, "imageUrl")}
          />

          {/* Reveal image (optional) — shown to the user ONLY after they
              answer correctly. The backend strips this from the today-response
              payload for users who haven't solved yet, so it's safe. */}
          <ImageUploadBlock
            label="Reveal image"
            hint='Shown only after a correct answer. For blurred-player challenges, upload the clean version here — mobile swaps from the teaser to this on the "Correct!" screen.'
            target="revealImageUrl"
            value={form.revealImageUrl}
            setValue={(v) => setField("revealImageUrl", v)}
            uploading={uploading === "revealImageUrl"}
            onUpload={(f) => void onUpload(f, "revealImageUrl")}
          />

          {/* MULTIPLE_CHOICE specific */}
          {form.type === "MULTIPLE_CHOICE" && (
            <div>
              <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
                Options <span className="text-red-500">*</span>{" "}
                <span className="font-normal text-gray-400">(select the correct one)</span>
              </Label>
              <div className="space-y-2">
                {form.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setField("correctOptionIndex", idx)}
                      className={cn(
                        "shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors",
                        form.correctOptionIndex === idx
                          ? "bg-green-500 border-green-500"
                          : "bg-white border-gray-300 hover:border-gray-400",
                      )}
                      title="Mark correct"
                    >
                      {form.correctOptionIndex === idx && (
                        <Check className="h-3.5 w-3.5 text-white" />
                      )}
                    </button>
                    <Input
                      value={opt}
                      onChange={(e) => setOption(idx, e.target.value)}
                      placeholder={`Option ${idx + 1}`}
                      className="flex-1"
                    />
                    {form.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(idx)}
                        className="shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remove option"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {form.options.length < 6 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={addOption}
                  className="mt-2 gap-1 text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50"
                >
                  <Plus className="h-3.5 w-3.5" /> Add option
                </Button>
              )}
            </div>
          )}

          {/* TEXT_ANSWER specific */}
          {form.type === "TEXT_ANSWER" && (
            <>
              {/* Answer-type selector — picks which dropdown the user sees on
                  mobile when answering. The question photo can be a player's
                  blurred face, a club crest, or a country flag — pick the
                  matching type so the mobile dropdown searches the right list. */}
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
                  Answer about <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.answerType}
                  onValueChange={(v) => {
                    // Reset the picked answer when switching types so we
                    // don't ship a player name into a club/nation slot.
                    setForm((f) => ({
                      ...f,
                      answerType: v as DcAnswerType,
                      correctText: "",
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PLAYER">Player</SelectItem>
                    <SelectItem value="CLUB">Club</SelectItem>
                    <SelectItem value="NATION">Country / Nation</SelectItem>
                    <SelectItem value="FREE_TEXT">Free text (anything)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-400 mt-1">
                  Mobile shows the matching searchable dropdown; matching stays text-based.
                </p>
              </div>

              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
                  Correct Answer <span className="text-red-500">*</span>
                </Label>
                {form.answerType === "PLAYER" && (
                  <PlayerSelect
                    value={form.correctText ? { name: form.correctText } : null}
                    onChange={(p) => setField("correctText", p?.name ?? "")}
                    placeholder="Search for a player…"
                  />
                )}
                {form.answerType === "CLUB" && (
                  <ClubSelect
                    value={form.correctText ? { name: form.correctText } : null}
                    onChange={(c) => setField("correctText", c?.name ?? "")}
                    placeholder="Search for a club…"
                  />
                )}
                {form.answerType === "NATION" && (
                  <NationSelect
                    value={form.correctText ? { name: form.correctText } : null}
                    onChange={(n) => setField("correctText", n?.name ?? "")}
                    placeholder="Search for a country / nation…"
                  />
                )}
                {form.answerType === "FREE_TEXT" && (
                  <Input
                    value={form.correctText}
                    onChange={(e) => setField("correctText", e.target.value)}
                    placeholder="Type the answer…"
                  />
                )}
                <p className="text-[11px] text-gray-400 mt-1">
                  {form.answerType === "FREE_TEXT"
                    ? "Use aliases below for alternate accepted phrasings."
                    : "Pick from the real list so the mobile dropdown matches."}
                </p>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
                  Accepted aliases{" "}
                  <span className="font-normal text-gray-400">(comma-separated)</span>
                </Label>
                <Input
                  value={form.aliases}
                  onChange={(e) => setField("aliases", e.target.value)}
                  placeholder={
                    form.answerType === "CLUB"
                      ? "barca, fcb, blaugrana"
                      : form.answerType === "NATION"
                        ? "england, eng, three lions"
                        : "ronaldo, cr7, cristiano"
                  }
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Matching is case-insensitive and ignores punctuation.
                </p>
              </div>
            </>
          )}

          {/* Clues */}
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
              Clues{" "}
              <span className="font-normal text-gray-400">
                (optional — one per line, unlocked by watching an ad)
              </span>
            </Label>
            <Textarea
              value={form.clues}
              onChange={(e) => setField("clues", e.target.value)}
              rows={3}
              className="resize-none font-mono text-sm"
              placeholder={"Born in Madeira, Portugal.\nJersey number 7.\nPlays for Al-Nassr."}
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Each clue reduces the user&apos;s eventual score by 20%.
            </p>
          </div>

          {/* Max points */}
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
              Max Points{" "}
              <span className="font-normal text-gray-400">(base score for a clean solve)</span>
            </Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={form.maxPoints}
              onChange={(e) => setField("maxPoints", parseInt(e.target.value, 10) || 10)}
              className="w-24"
            />
          </div>

          {/* Difficulty — surfaces as a coloured pill on the daily-challenge
              screen in mobile/web. Leave Unrated to hide the pill. */}
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-gray-700">
              Difficulty{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </Label>
            <Select
              value={form.difficulty ?? "UNRATED"}
              onValueChange={(v) =>
                setField(
                  "difficulty",
                  v === "UNRATED" ? null : (v as DcDifficulty),
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Unrated" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UNRATED">Unrated</SelectItem>
                <SelectItem value="EASY">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                    Easy
                  </span>
                </SelectItem>
                <SelectItem value="MEDIUM">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    Medium
                  </span>
                </SelectItem>
                <SelectItem value="HARD">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                    Hard
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400 mt-1">
              Shown to users as a coloured pill. Leave Unrated to hide.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            {isSaving
              ? "Saving…"
              : editing
                ? "Save Changes"
                : "Schedule Challenge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
